import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useRouterState } from '@tanstack/react-router'
import { Menu, LayoutDashboard, Users, Settings, Languages, LogOut, UtensilsCrossed, Package, AlertCircle, Calculator, Home, BookOpen } from 'lucide-react'
import { useCurrentUser, useLogout } from '@/hooks/useAuth'
import { useCurrentAssistant, useAssistantLogout, getAssistantRedirectUrl } from '@/hooks/useAssistantAuth'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,

  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AssistantPermissions } from '@/types/entities'

export function BurgerMenu() {
  const { t, i18n } = useTranslation()
  const { data: user } = useCurrentUser()
  const { data: assistant } = useCurrentAssistant()
  const assistantLogoutMutation = useAssistantLogout()
  const logoutMutation = useLogout()
  const [open, setOpen] = useState(false)
  const router = useRouterState()

  // Permission helper function
  const canViewPage = (page: keyof AssistantPermissions): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>
    return Object.values(pagePermissions).some((val) => val === true)
  }

  const isAssistant = !!assistant
  const isAdmin = user?.role === 'admin'

  const handleLogout = async () => {
    try {
      if (isAssistant) {
        await assistantLogoutMutation.mutateAsync()
        // Redirect to assistant login page with restaurant ID preserved
        window.location.href = getAssistantRedirectUrl()
      } else {
        await logoutMutation.mutateAsync()
        localStorage.removeItem('digimataam-selected-restaurant')
        window.location.href = '/'
      }
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value)
  }

  const currentPath = router.location.pathname
  const isRTL = i18n.dir() === 'rtl'

  // Map routes to permission keys (or null for always accessible routes)
  const routePermissionMap: Record<string, keyof AssistantPermissions | null> = {
    '/assistant-dashboard': null, // Always accessible for assistants
    '/dashboard': 'dashboard', // Admin dashboard - requires dashboard permission for assistants
    '/admin-cancellations': 'cancellations',
    '/staff': 'staff',
    '/menu': 'menu',
    '/inventory': 'stock',
    '/accounting': null, // Special handling - needs salaries OR expenses
  }

  // Check if assistant can view accounting (needs salaries OR expenses permission)
  const canViewAccounting = (): boolean => {
    return canViewPage('salaries') || canViewPage('expenses')
  }

  const menuItems = [
    // Home Page - only for assistants
    {
      to: '/assistant-dashboard',
      icon: Home,
      label: t('navigation.home'),
      assistantOnly: true,
    },
    // Dashboard
    {
      to: isAssistant ? '/dashboard' : '/',
      icon: LayoutDashboard,
      label: t('navigation.dashboard'),
      hideForAssistantWithoutPermission: true, // Will be filtered below
    },
    {
      to: '/admin-cancellations',
      icon: AlertCircle,
      label: t('navigation.cancellations'),
    },
    {
      to: '/staff',
      icon: Users,
      label: t('navigation.staff'),
    },
    {
      to: '/menu',
      icon: UtensilsCrossed,
      label: t('menu.title'),
    },
    {
      to: '/inventory',
      icon: Package,
      label: t('inventory.title'),
    },
    {
      to: '/accounting',
      icon: Calculator,
      label: t('accounting.title'),
      accountingPermission: true, // Special flag for accounting - needs salaries OR expenses
    },
    {
      to: '/settings',
      icon: Settings,
      label: t('navigation.settings'),
      adminOnly: true,
    },
    {
      to: '/assistant-settings',
      icon: Settings,
      label: t('navigation.settings'),
      assistantOnly: true,
    },
    {
      to: '/tutorial',
      icon: BookOpen,
      label: t('navigation.tutorial'),
      adminOnly: true,
    },
  ].filter(item => {
    // If assistant is logged in, check their permissions (but never show adminOnly items)
    if (isAssistant) {
      // Never show adminOnly items to assistants
      if (item.adminOnly) {
        return false
      }

      // Always show assistant-only items for assistants
      if ((item as any).assistantOnly) {
        return true
      }

      // Special handling for accounting - needs salaries OR expenses permission
      if ((item as any).accountingPermission) {
        return canViewAccounting()
      }

      const permissionKey = routePermissionMap[item.to]

      // If permission key is null (assistant-dashboard), always show
      if (permissionKey === null) {
        return true
      }

      // For other routes, check permissions
      if (permissionKey) {
        return canViewPage(permissionKey)
      }
      return false
    }

    // For admin users, filter out assistant-only items
    if (isAdmin) {
      if ((item as any).assistantOnly) {
        return false
      }
      return true
    }

    // For non-admin, non-assistant users, hide admin-only and assistant-only items
    if (item.adminOnly || (item as any).assistantOnly) {
      return false
    }

    return true
  })

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="shrink-0">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side={isRTL ? 'right' : 'left'} className="w-[280px] sm:w-[320px]">
        

        <div className="flex flex-col gap-4 py-6 flex-1 min-h-0">
          {/* Navigation Menu Items */}
          <nav className="flex flex-col gap-2">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = currentPath === item.to
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          {/* Divider */}
          <div className="border-t" />

          {/* Language Selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-3 text-sm font-medium">
              <Languages className="w-4 h-4" />
              <span>{t('navigation.language')}</span>
            </div>
            <Select value={i18n.language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ar">العربية</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Divider */}
          <div className="border-t" />

          {/* Logout Button */}
          {(user || assistant) && (
            <Button
              variant="ghost"
              className="justify-start gap-3 px-3"
              onClick={handleLogout}
              disabled={logoutMutation.isPending}
            >
              <LogOut className="w-5 h-5" />
              {t('navigation.logout')}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
